/**
 * «Mi ficha» en el portal del colaborador (plan del portal, fase 3).
 *
 * Los datos completos del trabajador en tres bloques —personales, trabajo,
 * previsión y pago— y el resumen de vacaciones. Es de **consulta** (D3): si un
 * dato está mal se pide la corrección a RRHH, no se edita acá.
 *
 * El encabezado ofrece el certificado de antigüedad (#1491). «Pedir corrección
 * de datos» sigue pendiente (fase 3b) y no se muestra, para no ofrecer un botón
 * que no hace nada.
 */
import { Link } from 'react-router-dom'
import { GlassPanel } from '@/components/ds/GlassPanel'
import { Skeleton } from '@/components/ds/Skeleton'
import { EmptyState } from '@/components/ds/EmptyState'
import { usePortalBase, usePortalFicha, usePortalInicio } from '../hooks/useEmployeePortal'
import { Dato, EncabezadoFicha, PlanSalud, SueldoBase } from '../components/FichaResumida'
import { BotonCertificadoAntiguedad } from '../components/BotonCertificadoAntiguedad'
import { antiguedadDesde, dias, fechaCorta, o } from '../components/formatos'

export default function MyProfilePage() {
  const base = usePortalBase()
  const { data: ficha, isLoading, isError } = usePortalFicha()
  const { data: inicio } = usePortalInicio()

  if (isError) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <GlassPanel><EmptyState variant="error" title="No se pudo cargar la ficha" /></GlassPanel>
      </div>
    )
  }
  if (isLoading || !ficha) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <GlassPanel><Skeleton variant="block" /></GlassPanel>
      </div>
    )
  }

  const { personales: p, trabajo: t, prevision: v } = ficha
  const areaDivision = [t.area, t.division].filter(Boolean).join(' · ')

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col gap-5">
      <h1 className="sr-only">Mi ficha</h1>
      <EncabezadoFicha ficha={ficha} grande acciones={<BotonCertificadoAntiguedad />} />

      <div className="grid gap-5 md:grid-cols-3">
        <GlassPanel as="section" aria-label="Datos personales" title="Datos personales">
          <dl className="flex flex-col gap-3.5">
            <Dato rotulo="RUT">{p.rut}</Dato>
            <Dato rotulo="Correo corporativo">{o(p.correo)}</Dato>
            <Dato rotulo="Teléfono">{o(p.telefono)}</Dato>
            <Dato rotulo="Fecha de nacimiento">{p.nacimiento ? fechaCorta(p.nacimiento) : '—'}</Dato>
            <Dato rotulo="Dirección">{o(p.direccion)}</Dato>
          </dl>
        </GlassPanel>

        <GlassPanel as="section" aria-label="Mi trabajo" title="Mi trabajo">
          <dl className="flex flex-col gap-3.5">
            <Dato rotulo="Jefatura directa">{o(t.jefatura)}</Dato>
            <Dato rotulo="Área · División">{o(areaDivision)}</Dato>
            <Dato rotulo="Centro de costo">{o(t.centro_de_costo)}</Dato>
            <Dato rotulo="Jornada">{t.jornada_semanal ? `${Number(t.jornada_semanal)} horas semanales` : '—'}</Dato>
            <Dato rotulo="Contrato">
              {t.contrato
                ? `${t.contrato}${t.contrato_termino ? `, hasta el ${fechaCorta(t.contrato_termino)}` : ''}`
                : '—'}
            </Dato>
            <Dato rotulo="Ingreso">{t.ingreso ? `${fechaCorta(t.ingreso)} (${antiguedadDesde(t.ingreso)})` : '—'}</Dato>
          </dl>
        </GlassPanel>

        <GlassPanel as="section" aria-label="Previsión y pago" title="Previsión y pago">
          <dl className="flex flex-col gap-3.5">
            <Dato rotulo="AFP">{o(v.afp)}</Dato>
            <Dato rotulo="Salud">{o(v.salud)}</Dato>
            {v.plan_salud_uf && (
              <Dato rotulo="Plan de salud"><PlanSalud ficha={ficha} /></Dato>
            )}
            <Dato rotulo="Cuenta de pago">{o(v.cuenta)}</Dato>
            <Dato rotulo="Sueldo base"><SueldoBase ficha={ficha} /></Dato>
            <Dato rotulo="Cargas familiares">{v.cargas_familiares ?? 0}</Dato>
          </dl>
        </GlassPanel>
      </div>

      {inicio && (
        <GlassPanel as="section" aria-label="Resumen de vacaciones" className="flex flex-wrap items-center gap-x-10 gap-y-4">
          <div>
            <p className="text-xs font-semibold text-content-muted">Feriado legal</p>
            <p className="text-2xl font-bold text-content tabular-nums">{dias(inicio.vacaciones.legales)} días</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-content-muted">Feriado progresivo</p>
            <p className="text-2xl font-bold text-content tabular-nums">{dias(inicio.vacaciones.progresivas)} días</p>
          </div>
          <div className="flex-1" />
          <Link
            to={`${base}/time-off`}
            className="inline-flex rounded-lg bg-primary hover:bg-primary-hover px-4 py-2 text-sm font-semibold text-on-primary"
          >
            Solicitar vacaciones
          </Link>
        </GlassPanel>
      )}

      <p className="text-xs text-content-muted">
        Si algún dato no corresponde, hay que avisar a RRHH para que lo corrija.
      </p>
    </div>
  )
}
